import { Component, Inject, OnInit } from '@angular/core';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatOption } from '@angular/material/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormField, MatHint, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { DialogAction, DialogService, FormFieldComponent, NotificationService, NotificationType } from '@ird/ng-base';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { map, Observable, startWith, Subscription } from 'rxjs';
import { DocumentFileType, FileDetails, FileWithDetails } from '../../models';
import { UserService } from '../../services';
import { FileUploaderComponent } from '../file-uploader/file-uploader.component';
import { AsyncPipe } from '@angular/common';
import { ArraySerializer } from '@ird/shared-utils';
import { LanguageMultiselectComponent } from '../language-multiselect/language-multiselect.component';

export interface DialogData {
  uploadType: DocumentFileType;
  uploadMethod: (fileDetails: FileWithDetails, type: DocumentFileType) => Observable<any>;
}

@Component({
  selector: 'shared-upload-dialog',
  templateUrl: './upload-dialog.component.html',
  styleUrls: ['./upload-dialog.component.scss'],
  imports: [
    FileUploaderComponent,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatSelect,
    MatOption,
    TranslatePipe,
    FlexLayoutModule,
    FormFieldComponent,
    AsyncPipe,
    MatHint,
    LanguageMultiselectComponent,
  ],
})
export class UploadDialogComponent implements OnInit {
  public readonly types: DocumentFileType[];
  public fileFormGroup: FormGroup;
  public file: File;
  public descriptionLength$: Observable<number>;
  public languages: string[];
  private readonly arraySerializer = ArraySerializer;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      dialogData: DialogData;
      actions: DialogAction[];
    },
    private dialogService: DialogService,
    private formBuilder: FormBuilder,
    private userService: UserService,
    private notificationService: NotificationService,
    private translate: TranslateService,
  ) {
    this.data.actions = [
      {
        type: 'mat-stroked-button',
        color: 'primary',
        text: 'UPLOAD_DIALOG_BUTTON_CLOSE',
        action: () => {
          this.dialogService.close();
          return Subscription.EMPTY;
        },
      },
      {
        type: 'mat-raised-button',
        color: 'primary',
        text: 'UPLOAD_DIALOG_BUTTON_UPLOAD',
        action: () => {
          const fileDetails = this.getFileDetails();
          const type = this.fileFormGroup.controls.type.value ?? this.types[0];
          return this.data.dialogData.uploadMethod(fileDetails, type).subscribe({
            next: this.handleSuccess,
            error: this.handleError,
          });
        },
        disable: () => {
          return this.fileFormGroup.statusChanges.pipe(map((status) => status === 'INVALID' || !this.file));
        },
      },
    ];

    const internalEmployee = this.userService.getUser().isInternal;
    const types = [DocumentFileType.USER, DocumentFileType.MANUFACTURER, DocumentFileType.GATED_DOCUMENT];
    if (internalEmployee) {
      types.push(DocumentFileType.INTERNAL);
    }
    this.types = types;
    this.languages = [...this.translate.getLangs()];
  }

  ngOnInit(): void {
    this.fileFormGroup = this.formBuilder.group({
      fileName: new FormControl({ value: null, disabled: true }, Validators.required),
      displayName: new FormControl(null, Validators.required),
      type: new FormControl(this.data.dialogData.uploadType ?? this.types[0], Validators.required),
      description: new FormControl(null),
      category: new FormControl(null),
      language: new FormControl(null),
      file: new FormControl(null, Validators.required),
    });
    this.descriptionLength$ = this.fileFormGroup.get('description').valueChanges.pipe(
      startWith(''),
      map((value: string) => value?.length ?? 0),
    );
  }

  onFileChange(file: File) {
    this.file = file;
    if (file) {
      const lastIndex = file.name.lastIndexOf('.');
      if (!this.fileFormGroup.controls.displayName.value) {
        this.fileFormGroup.controls.displayName.setValue(file.name.substring(0, lastIndex));
      }
      this.fileFormGroup.controls.fileName.setValue(file.name);
      this.fileFormGroup.controls['file'].setValue(file);
    } else {
      this.fileFormGroup.controls.displayName.setValue(null);
      this.fileFormGroup.controls.fileName.setValue(null);
      this.fileFormGroup.controls['file'].setValue(null);
    }
  }

  private getFileDetails(): FileWithDetails {
    const languageValue = this.fileFormGroup.controls.language.value;
    return {
      file: this.file,
      details: {
        displayName: this.fileFormGroup.controls.displayName.value,
        description: this.fileFormGroup.controls.description.value,
        fileName: this.file.name,
        type: this.fileFormGroup.controls.type.value,
        metadata: {
          CATEGORY_TYPE: this.fileFormGroup.controls.category.value,
          DOCUMENT_TYPE: this.fileFormGroup.controls.type.value,
          LANG: languageValue ? this.arraySerializer.serialize(languageValue) : '',
        },
      } as FileDetails,
    };
  }

  /**
   * Handles file upload success
   *
   * @param file Uploaded file
   */
  private handleSuccess = (file: File): void => {
    this.dialogService.close(file);
    this.notificationService.showNotification({
      message: 'UPLOAD_DIALOG.UPLOAD_SUCCESS',
      type: NotificationType.Success,
    });
  };

  /**
   * Handles file upload error
   */
  private handleError = (): void => {
    this.notificationService.showNotification({
      message: 'UPLOAD_DIALOG.UPLOAD_ERROR',
      type: NotificationType.Error,
    });
  };
}
